/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, NavigateFunction } from 'react-router-dom'
import moment from 'moment'

import { DriverNode, useDriverQuery } from '@/apollo/graphql'
import { homePath, transformData } from '@/utils'
import { useAPI, useToast } from '@/hooks'
import { TFormSubmit } from '@/types'
import { t } from '@/i18n'

interface IUseData {
  id?: string
}

interface IUseDataRes {
  loading: boolean
  submitting: boolean
  data: Partial<DriverNode> | undefined
  onSubmit: TFormSubmit<{ driver: DriverNode; driver_id?: string }>
  onCancel: () => void
  onDelete: (cb: () => void) => void
}

export const useData = ({ id }: IUseData): IUseDataRes => {
  const addToast = useToast()
  const navigate: NavigateFunction = useNavigate()
  const { submitting, sendForm } = useAPI()
  const [data, setData] = useState<DriverNode>()

  const { loading, refetch } = useDriverQuery({
    skip: !id,
    fetchPolicy: 'network-only',
    variables: {
      id: id || '',
    },
    onCompleted: res => {
      if (res.driver) {
        // @ts-ignore
        setData(res.driver)
      }
    },
  })

  useEffect(() => {
    if (!id) {
      setData(undefined)
    }
  }, [id])

  const onSubmit: TFormSubmit<{ driver: DriverNode; driver_id?: string }> =
    useCallback(
      (path, isEdit, callback) => d => {
        if (d.driver.birthday) {
          d.driver.birthday = moment(d.driver.birthday).format('YYYY-MM-DD')
        }

        sendForm(
          {
            path,
            method: isEdit ? 'PUT' : 'POST',
            data: {
              ...(transformData(d.driver) || {}),
              driver_id: d.driver_id,
            },
            callback: e => {
              callback?.(e)
              refetch()
            },
          },
          { showNotify: true },
        )
      },
      [refetch, sendForm],
    )

  const onCancel = useCallback(() => {
    navigate(homePath)
  }, [navigate])

  const onDelete = useCallback(
    (cb: () => void) => {
      sendForm(
        {
          path: `cars/drivers/${id}/`,
          method: 'DELETE',
          data: undefined,
          callback: () => {
            cb()
            refetch()
            addToast('success', t('success'))
          },
        },
        {
          showNotify: false,
        },
      )
    },
    [addToast, sendForm, id, refetch],
  )

  return {
    loading,
    submitting,
    data,
    onSubmit,
    onCancel,
    onDelete,
  }
}
